import React, {
    ReactElement, useEffect, useRef, useState,
} from "react";
import {
    DragDropContext, Droppable, DropResult,
} from "react-beautiful-dnd";
import clientApi from "../../api";
import { Loading } from "../../components";
import { PRIORITY_TEXT } from "../../components/agent/card/Card";
import Column from "../../components/agent/column/Column";
import EasterEgg from "../../components/agent/easter/EasterEgg";
import EndChat from "../../components/agent/end-chat/EndChat";
import Ignore from "../../components/agent/ignore/Ignore";
import LatestConversations from "../../components/agent/latest-conversations/LatestConversations";
import MarkRead from "../../components/agent/mark-read/MarkRead";
import MarkUnread from "../../components/agent/mark-unread/MarkUnread";
import ParticipantsAdd from "../../components/agent/participants/ParticipantsAdd";
import ParticipantsLeave from "../../components/agent/participants/ParticipantsLeave";
import SearchResults from "../../components/agent/search-results/SearchResults";
import SetTicketState from "../../components/agent/set-ticket-state/SetTicketState";
import TopOptions from "../../components/agent/top-settings/TopSettings";
import Transfer from "../../components/agent/transfer/Transfer";
import Snooze from "../../components/agent/snooze/Snooze";
import Hold from "../../components/agent/hold/Hold";
import Chat from "../../components/chat/Chat";
import Confirm from "../../components/confirm/Confirm";
import StartConversation from "../../components/start-conversation/StartConversation";
import useNotifications from "../../contexts/NotificationContext";
import useEvents, { ConnectionEvent } from "../../contexts/PortalSubscriptionContext";
import {
    ChatStatus, DashboardGroupBy,
    DashboardSettings,
    DashboardSortBy,
    DashboardTicketState, DashboardView, GetChatDocument, ListAgentChatDocument, PortalChatEvent,
    PortalEventType,
    PortalMessageEvent,
    PortalParticipantEvent,
    SearchFiltersInput,
    SenderType,
    TicketPriority, TicketView, useListAgentChatQuery,
    useListStatesQuery,
    useUpdateDashboardMutation,
    PortalSnoozeEvent,
} from "../../__generated__/api";
import { NoDataOrError } from "../error/Error";
import Styles from "./Agent.module.scss";
import ColumnStyles from "./Card.module.scss";
import DropAction, { Action } from "./DropAction";
import useSelectionState from "../../contexts/selection-state/SelectionStateContext";
import {
    ColumnData, ColumnGroup, MoveDirection, Ticket,
} from "../../contexts/selection-state/selection-state";

const PriorityOrder = {
    [TicketPriority.Critical]: 0,
    [TicketPriority.High]: 1,
    [TicketPriority.Medium]: 2,
    [TicketPriority.Low]: 3,
};

const GROUP_BY_FUNCTION = {
    [DashboardGroupBy.Assignee]: {
        groupBy: (item: Ticket) => item.assigneeId ?? "",
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.assigneeId ?? "",
            title: item.isAssignedToCurrentUser ? "You" : (item.assignee?.name ?? "Unassigned"),
            hideImage: item.assignee == null,
            imageUrl: item.assignee?.avatarUrl as string | undefined,
            subtitle: item.assignee?.group as string | undefined,
        }),
        sortGroups: (ticket: Ticket) => ticket.assignee?.name,
    },
    [DashboardGroupBy.Priority]: {
        groupBy: (item: Ticket) => item.priority ?? TicketPriority.Medium,
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.priority as string,
            title: PRIORITY_TEXT[item.priority ?? TicketPriority.Medium],
            hideImage: true,
            isCentered: true,
        }),
        sortGroups: (ticket: Ticket) => PriorityOrder[ticket.priority ?? TicketPriority.Medium],
    },
    [DashboardGroupBy.Channel]: {
        groupBy: (item: Ticket) => item.source?.id ?? "",
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.source?.id as string,
            title: item.source?.text ?? "Unknown",
            hideImage: true,
            // imageUrl: item.creator.avatarUrl as string | undefined,

        }),
        sortGroups: (ticket: Ticket) => ticket.source?.text,
    },
    [DashboardGroupBy.User]: {
        groupBy: (item: Ticket) => item.creatorId,
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.creatorId,
            title: item.creator.name ?? "",
            imageUrl: item.creator.avatarUrl as string | undefined,
            subtitle: item.creator.group as string | undefined,
        }),
        sortGroups: (ticket: Ticket) => ticket.creator.name,
    },
    [DashboardGroupBy.CompanyGroup]: {
        groupBy: (item: Ticket) => item.creator.group ?? "",
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.creator.group ?? "",
            title: item.creator.group ?? "Group unknown",
            hideImage: true,
        }),
        sortGroups: (ticket: Ticket) => ticket.assignee?.name,
    },
    [DashboardGroupBy.Queue]: {
        groupBy: (item: Ticket) => item.ticket?.queue?.id ?? "",
        createGroup: (item: Ticket): Partial<ColumnGroup> => ({
            id: item.ticket?.queue?.id ?? "",
            title: item.ticket?.queue?.name ?? "No Queue",
            hideImage: true,
        }),
        sortGroups: (ticket: Ticket) => ticket.assignee?.name,
    },
    [DashboardGroupBy.NoGroup]: undefined,
};

function createGroup(
    groupBy: DashboardGroupBy,
    first: Ticket,
    state: DashboardTicketState | undefined,
    items?: Ticket[],
): ColumnGroup {
    const groupHandler = GROUP_BY_FUNCTION[groupBy];
    const created = groupHandler ? groupHandler.createGroup(first) : {};
    created.fullId = `${state?.ticketStateId}|${created.id}`;
    if (items) {
        created.items = items;
    }
    return created as ColumnGroup;
}

function getDateField(ticket: Ticket, field: keyof Ticket) {
    const value = ticket[field];
    if (!value) {
        return 0;
    }
    return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

const SORT_FUNCTION: Record<DashboardSortBy, ((value: Ticket) => number) | undefined> = {
    [DashboardSortBy.CreatedDateAscending]: row => getDateField(row, "createdDate"),
    [DashboardSortBy.CreatedDateDescending]: row => -1 * getDateField(row, "createdDate"),

    [DashboardSortBy.LastResponseDateAscending]: row => getDateField(row, "lastUserMessageDate"),
    [DashboardSortBy.LastResponseDateDescending]: row => -1 * getDateField(row, "lastUserMessageDate"),

    [DashboardSortBy.PriorityAscending]: value => PriorityOrder[value.priority ?? TicketPriority.Medium],
    [DashboardSortBy.PriorityDescending]: value => -1 * PriorityOrder[value.priority ?? TicketPriority.Medium],

    [DashboardSortBy.TicketNumberAscending]: value => value.ticketNumber ?? 0,
    [DashboardSortBy.TicketNumberDescending]: value => -1 * (value.ticketNumber ?? 0),

    [DashboardSortBy.Manual]: undefined,

};

function addColumn(state: DashboardTicketState, items: Ticket[], groupBy: DashboardGroupBy, sortBy: DashboardSortBy): ColumnData {
    let itemsSorted = (items ?? []);

    const sortHandler = SORT_FUNCTION[sortBy];
    if (sortHandler) {
        itemsSorted = itemsSorted.sortBy(sortHandler);
    }
    const groupHandler = GROUP_BY_FUNCTION[groupBy];
    let groups: Ticket[][];
    if (groupHandler) {
        groups = Object.values(itemsSorted.groupBy(row => groupHandler.groupBy(row)));
    } else if (itemsSorted.isNotEmpty) {
        groups = [itemsSorted];
    } else {
        groups = [];
    }

    if (groupHandler?.sortGroups) {
        groups = groups.sortBy(row => groupHandler.sortGroups(row.firstElement));
    }
    return {
        id: state.ticketStateId,
        state,
        text: state.label ?? state.ticketState.name,
        description: state.ticketState.description as string,
        items: groups.flatten(),
        statuses: new Set(state.ticketState.chatStatuses),
        groups: groups.map(group => createGroup(groupBy, group.firstElement, state, group)),
    };
}

export default function Agent(): ReactElement {
    const notifications = useNotifications();
    const selectionState = useSelectionState();

    const [dragGroup, setDragGroup] = useState<string | undefined>();
    const [dragParentStateId, setDragParentColumn] = useState<string | undefined>();

    const [showActionStates, setShowActionStates] = useState<boolean>();

    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const [hasSlas, setHasSla] = useState<boolean>();

    const [search, setSearch] = useState(() => "");
    const [searchFilters, setSearchFilters] = useState<SearchFiltersInput>();

    const [dashboardId, setDashboardId] = useState<string>();
    const messageIds = useRef<string[]>([]);

    const {
        error: statesError, loading: isStateLoading, data: statesData, refetch: refreshColumns,
    } = useListStatesQuery();

    const {
        error: chatError, loading: isChatLoading, data: chatDataRaw, refetch: refreshData,
    } = useListAgentChatQuery({
        // fetchPolicy: "cache-first",
        skip: !statesData?.dashboard,
        variables: {
            filter: statesData?.dashboard?.settings?.ticketView,
            dashboardId: statesData?.dashboard?.id,
        },
    });

    const isSearching = (search || (searchFilters && Object.values(searchFilters).filterNull().isNotEmpty));

    const [view, setView] = useState(() => statesData?.dashboard?.settings?.view ?? DashboardView.Kanban);
    useEffect(() => {
        setView(isSearching
            ? DashboardView.Compact
            : statesData?.dashboard?.settings?.view ?? DashboardView.Kanban);
    }, [search, statesData, searchFilters]);

    const [updateDashboard] = useUpdateDashboardMutation();

    useEffect(() => {
        if (!isChatLoading && statesData?.dashboard?.ticketStates.isNotEmpty) {
            refreshData();
        }
    }, [statesData?.dashboard?.ticketStates]);

    const [columns, setColumns] = useState<ColumnData[]>([]);

    useEvents(api => {
        api.on(ConnectionEvent.RECONNECTED, 0, () => {
            refreshData();
        });
        api.on(PortalEventType.Resync, 0, () => {
            refreshData();
        });
        api.on(PortalEventType.ConversationNew, 0, (event: PortalChatEvent) => {
            if (!event.chat) {
                return;
            }
            const { status } = event.chat;
            const column = selectionState.findColumn(col => col.statuses.has(status));
            if (!column) {
                return;
            }

            column.items.push(event.chat);
            notifications.conversation(event.chatId, "New Chat", `Subject: ${event.chat.subject}`);
            // selectionState.setColumns(columns);
            selectionState.addNotification(event.chat.id);
        });
        api.on(PortalEventType.SnoozeStopped, 0, (event: PortalSnoozeEvent) => {
            if (!event.chatId) {
                return;
            }
            const subject = selectionState.getChat(event.chatId)?.subject;

            notifications.message(`${event.chatId}-unsnooze`, `Message Unsnoozed`, subject ?? `Message was unsnoozed`);
            refreshData();
            selectionState.addNotification(event.chatId);
            selectionState.markCacheUnread(event.chatId);
        });
        api.on(PortalEventType.ParticipantsAdded, 0, (event: PortalParticipantEvent) => {
            if (!event.chat) {
                return;
            }
            const { status } = event.chat;
            const column = selectionState.findColumn(col => col.statuses.has(status));
            if (!column) {
                return;
            }
            // clientApi.refetchQueries({
            //     include: [
            //         GetChatDocument,
            //     ],
            // });

            if (event.chat) {
                column.items.push(event.chat);
            }
            notifications.conversation(event.chatId, "New Participating Chat", `Subject: ${event.chat?.subject}`);
            // selectionState.setColumns(columns);
            selectionState.addNotification(event.chat?.id);
        });
        api.on(PortalEventType.ParticipantsRemoved, 0, (event: PortalParticipantEvent) => {
            if (!selectionState.hasChat(event.chatId)) {
                return;
            }

            clientApi.refetchQueries({
                include: [
                    GetChatDocument,
                ],
            });

            refreshData();
        });
        // api.on(PortalEventType.ConversationIgnored, 0, (event: PortalChatEvent) => {
        //     selectionState.delete(event.chatId);
        // });
        api.on(PortalEventType.ConversationAssigned, 0, () => {
            refreshData();
        });
        api.on(PortalEventType.MessageNew, 0, (msg: PortalMessageEvent) => {
            const item = selectionState.getChat(msg.chatId);

            if (!item) {
                messageIds.current = [...messageIds.current, msg.chatId];
                return;
            }

            const isNotAgent = msg.sender?.type !== SenderType.Agent;
            if (isNotAgent) {
                notifications.message(msg.messageId, `From: ${msg.sender?.name}`, msg.message.plaintext as string);
            }

            if (item) {
                item.lastMessage = msg.message.plaintext;
                item.lastUserMessageDate = new Date();
                selectionState.setChat(item.id, item);
                // clientApi.writeFragment({
                //     id: item.id,
                //     data: item,
                //     fragment: ChatViewFragmentDoc,
                // })

                clientApi.writeQuery({
                    data: {
                        chats: chatDataRaw?.chats?.chats?.filter(row => row.id !== item.id).concat([item]),
                    },
                    query: ListAgentChatDocument,
                    variables: {
                        filter: statesData?.dashboard?.settings?.ticketView,
                        dashboardId,
                    },
                });
            }

            if (msg.chatId !== selectionState.currentId) {
                selectionState.addNotification(msg.chatId);
            }
        });
    });

    useEffect(() => {
        let chats = chatDataRaw?.chats?.chats?.map(chat => ({ ...chat })).filter(row => row.id);
        if (search && !chats) {
            return;
        }
        setDashboardId(statesData?.dashboard?.id);
        if (search) {
            const searchStr = search.toLowerCase();
            chats = chats?.filter(row => row.subject?.toLowerCase().includes(searchStr)
                            || row.creator?.name?.toLowerCase().includes(searchStr)
                            || row.creator?.group?.toLowerCase().includes(searchStr)
                            || row.holdNotes?.toLowerCase().includes(searchStr)
                            || row.lastMessage?.toLowerCase().includes(searchStr)
                            || row.priority?.toString().toLowerCase().includes(searchStr)
                            || row.status?.toString().toLowerCase().includes(searchStr));
        }
        if (searchFilters?.endDate) {
            const end = (searchFilters.endDate as Date).getTime();
            chats = chats?.filter(row => (row.createdDate ? new Date(row.createdDate).getTime() : end) < end);
        }
        if (searchFilters?.startDate) {
            const start = (searchFilters.startDate as Date).getTime();
            chats = chats?.filter(row => (row.createdDate ? new Date(row.createdDate).getTime() : 0) > start);
        }
        if (searchFilters?.priority) {
            const priority = searchFilters?.priority;
            chats = chats?.filter(row => (row.priority ?? TicketPriority.Medium) === priority);
        }
        if (searchFilters?.status) {
            const status = searchFilters?.status;
            chats = chats?.filter(row => row.ticket?.status === status);
        }
        if (searchFilters?.formIds) {
            const formIds = searchFilters?.formIds;
            chats = chats?.filter(row => formIds.some(formId => row.ticket?.formId === formId));
        }
        if (searchFilters?.tags) {
            const tags = searchFilters?.tags;
            chats = chats?.filter(row => tags.union(row.ticket?.tags ?? []).length > 0);
        }
        if (searchFilters?.assignedAgentId) {
            const assignedAgentId = searchFilters?.assignedAgentId;
            chats = chats?.filter(row => row.assigneeId === assignedAgentId);
        }

        if (searchFilters?.creatorId) {
            const creatorId = searchFilters?.creatorId;
            chats = chats?.filter(row => row.creatorId === creatorId);
        }
        if (searchFilters?.sourceIds?.isNotEmpty) {
            const sourceIds = new Set(searchFilters?.sourceIds);
            chats = chats?.filter(row => sourceIds.has(row.source?.id));
        }
        if (searchFilters?.queueIds?.isNotEmpty) {
            const queueIds = new Set(searchFilters?.queueIds);
            chats = chats?.filter(row => queueIds.has(row.ticket?.queueId));
        }
        setHasSla(chats?.some(chat => chat.ticket?.dueDates?.isNotEmpty));

        const cols: ColumnData[] = [];
        const ticketStates = statesData?.dashboard?.ticketStates?.sortBy(state => state.locationIndex)
            .dedupe(state => state.ticketStateId);
        const groupBy = statesData?.dashboard?.settings?.groupBy ?? DashboardGroupBy.User;
        const sortBy = statesData?.dashboard?.settings?.sortBy ?? DashboardSortBy.CreatedDateDescending;

        if (view === DashboardView.Wall) {
            let itemsSorted = (chats ?? []);

            const sortHandler = SORT_FUNCTION[sortBy];
            if (sortHandler) {
                itemsSorted = itemsSorted.sortBy(sortHandler);
            }
            const groupHandler = GROUP_BY_FUNCTION[groupBy];
            let groups: Ticket[][];
            if (groupHandler) {
                groups = Object.values(itemsSorted.groupBy(row => groupHandler.groupBy(row)));
            } else if (itemsSorted.isNotEmpty) {
                groups = [itemsSorted];
            } else {
                groups = [];
            }

            groups.forEach(list => {
                if (!groupHandler) {
                    return;
                }
                const first = list.firstElement;
                const group = createGroup(groupBy, first, undefined, list);
                // group.fullId += i;
                cols.push({
                    id: group.id as string,
                    groups: [group as ColumnGroup],
                    items: list,
                    state: undefined,
                    statuses: new Set(),
                    text: group.title,
                    description: group.subtitle,
                });
            });
        } else {
            const ticketStatesMap = ticketStates?.toIdMap("ticketStateId") ?? {};
            chats?.forEach(chat => {
                if (!chat.stateId) {
                    return;
                }

                const state = ticketStatesMap[chat.stateId];
                if (!state) {
                    return;
                }
                const statuses = state.ticketState.chatStatuses.isNotEmpty
                    ? state.ticketState.chatStatuses
                    : [ChatStatus.Assigned];
                const currentStatus = selectionState.getChatStatus(chat.id, chat);
                if (state && !statuses.some(status => status === currentStatus)) {
                    // eslint-disable-next-line no-param-reassign
                    chat.stateId = null;
                }
            });
            const chatDataMap = chats?.groupBy(id => id.stateId?.toString()) ?? {};
            // Resync doesnt work here because its getting chat status (whic hs now finished)
            // instead of the chat status from the updated chat
            const chatStatusMap = chats?.filter(chat => !chat.stateId)
                .groupBy(chat => selectionState.getChatStatus(chat.id, chat) ?? chat.status) ?? {};

            const seen = new Set();
            ticketStates?.forEach(state => {
                const ticketsByStatus = state.ticketState.chatStatuses.map(status => chatStatusMap[status] ?? []);
                const tickets = (chatDataMap[state.ticketStateId] ?? []).concat(...ticketsByStatus)
                    .filter(ticket => !seen.has(ticket.id));

                tickets.forEach(ticket => seen.add(ticket.id));
                cols.push(
                    addColumn(
                        state,
                        tickets as Ticket[],
                        groupBy,
                        sortBy,
                    ),
                );
            });
        }

        setColumns(cols);
        selectionState.setMoveDirection(view === DashboardView.Compact ? MoveDirection.VERTICAL : MoveDirection.HORIZONTAL);
        selectionState.setColumns(cols);
    }, [chatDataRaw, statesData, selectionState, search, searchFilters]);

    useEffect(() => {
        messageIds.current.forEach(messageId => {
            selectionState.addNotification(messageId);
        });

        messageIds.current.splice(0, messageIds.current.length);

        // refreshData();
    }, [columns]);

    const updateColumns = async (settings: Partial<DashboardSettings> = {}) => {
        const updatedState = columns.map((col, index) => ({
            ticketStateId: col.id,
            locationIndex: index,
            label: col.text,
        }));
        setColumns(columns);
        selectionState.setMoveDirection(settings.view === DashboardView.Compact
            ? MoveDirection.VERTICAL
            : MoveDirection.HORIZONTAL);

        await updateDashboard({
            variables: {
                dashboard: {
                    settings: Object.keys(settings).isNotEmpty ? settings : undefined,
                    ticketStates: updatedState,
                },
            },
        });
        refreshColumns();
    };

    useEffect(() => {
        selectionState.registerDeleteHandler(tickets => {
            const set = new Set(tickets.toIdList("id"));
            setColumns(cols => cols.map(column => {
                const col = column;
                col.groups = col.groups.mapFilter(group => {
                    const copy = group;
                    copy.items = group.items.filter(row => !set.has(row.id));
                    return copy.items.isNotEmpty ? copy : undefined;
                });
                col.items = col.groups.flatMap(group => group.items);
                return col;
            }));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isStateLoading || (isChatLoading && !chatDataRaw?.chats)) {
        return <Loading />;
    }

    if (chatError || !chatDataRaw) {
        return <NoDataOrError error={chatError} />;
    }
    if (statesError || !statesData) {
        return <NoDataOrError error={statesError} />;
    }

    const onDragEnd = async (result: DropResult) => {
        const { draggableId } = result;
        setShowActionStates(false);
        if (!result.destination) {
            setDragGroup(undefined);
            setDragParentColumn(undefined);
            return;
        }

        if (draggableId.startsWith("column-urn:fq:ticket-state")) {
            const column = columns[result.source.index];
            columns.splice(result.source.index, 1);
            columns.splice(result.destination.index, 0, column);

            await updateColumns();
            return;
        }

        const chat = selectionState.getChat(draggableId);
        if (result.destination.droppableId?.startsWith("action")) {
            const action = result.destination.droppableId.split(":")[1];
            if (action === Action.IGNORE) {
                selectionState.ignoreSelected();
            } else if (action === Action.TRANSFER) {
                selectionState.tranferSelected();
            } else if (action === Action.ASSIGN) {
                selectionState.assignSelected();
            } else if (action === Action.END_CHAT) {
                selectionState.endChatSelected();
            }
            return;
        }

        if (!chat) {
            setDragGroup(undefined);
            setDragParentColumn(undefined);
            return;
        }

        const isSameColumn = result.destination.droppableId === result.source.droppableId;

        const ticketStateId = result.destination?.droppableId.split("|")[0];
        chat.stateId = ticketStateId;
        const state = statesData.dashboard?.ticketStates.find(current => current.ticketStateId === ticketStateId);

        const sourceGroup = columns.flatMap(col => col.groups).find(group => group.fullId === result.source.droppableId);
        const destinationId = result.destination?.droppableId;

        if (!chat || !sourceGroup) {
            setDragGroup(undefined);
            setDragParentColumn(undefined);
            return;
        }
        if (chat.status === ChatStatus.ChatRequested && state?.ticketState.chatStatuses.includes(ChatStatus.Finished)) {
            // Show error message.
            setErrorMessage("Sorry, you can not drag a requested item directly to a finished state. "
            + "You can either ignore it or assign it to yourself then close.");
            setDragGroup(undefined);
            setDragParentColumn(undefined);
            return;
        }

        const sourceColumn = columns.find(col => col.groups.some(group => group === sourceGroup));

        const groupBy = statesData?.dashboard?.settings?.groupBy ?? DashboardGroupBy.User;
        const sortBy = statesData?.dashboard?.settings?.sortBy ?? DashboardSortBy.CreatedDateDescending;

        const selectedChats = selectionState.getSelection().filter(row => row.canCurrentUserEdit
            || row.status === ChatStatus.Connected
            || row.status === ChatStatus.ChatRequested);

        if (selectedChats.isEmpty) {
            setDragGroup(undefined);
            setDragParentColumn(undefined);
            return;
        }

        const selectedSet = new Set(selectedChats.toIdList("id"));
        if (sourceColumn && sourceColumn.state) {
            const items = sourceColumn.items.filter(row => !selectedSet.has(row.id));
            const newColumn = addColumn(sourceColumn.state, items, groupBy, sortBy);
            sourceColumn.items = newColumn.items;
            sourceColumn.groups = newColumn.groups;
        }

        let destinationGroup = columns.flatMap(col => col.groups).find(group => group.fullId === destinationId);
        let destinationColumn = destinationGroup
            ? columns.find(col => col.groups.some(group => group === destinationGroup))
            : undefined;

        let allItems: Ticket[] | undefined;
        if (!destinationGroup) {
            destinationColumn = columns.find(col => col.state?.ticketStateId === destinationId);
            if (destinationColumn) {
                destinationColumn.items.splice(result.destination.index, 0, ...selectedChats);
                allItems = destinationColumn.items;
            }
        } else {
            destinationGroup.items.splice(result.destination.index, 0, ...selectedChats);
            if (destinationColumn) {
                allItems = destinationColumn.groups.flatMap(col => col.items);
            }
        }
        const newSortBy = sourceGroup === destinationGroup ? DashboardSortBy.Manual : sortBy;
        if (destinationColumn && allItems && destinationColumn.state) {
            const newColumn = addColumn(destinationColumn.state, allItems, groupBy, newSortBy);
            destinationColumn.items = newColumn.items;
            destinationColumn.groups = newColumn.groups;
        }

        setColumns(columns);
        if (!destinationGroup) {
            destinationGroup = columns.flatMap(col => col.groups).find(group => group.fullId === destinationId);
        }

        if (state && !isSameColumn) {
            const ids = selectedChats.toIdList("id");
            selectionState.setChatState(ids, state, destinationGroup as ColumnGroup);
            selectedChats.forEach(ticket => {
                // eslint-disable-next-line no-param-reassign
                ticket.stateId = ticketStateId;
            });
            const statuses = new Set(state.ticketState.chatStatuses);
            if (statuses.has(ChatStatus.Finished)) {
                selectionState.endChat(...ids);
            } else if (statuses.has(ChatStatus.Ignored)) {
                selectionState.ignore(...ids);
            } else if (statuses.has(ChatStatus.Assigned)) {
                selectionState.assignSelf(...ids);
            } else if (statuses.has(ChatStatus.ChatRequested)) {
                selectionState.unassign(...ids);
            } else if (statuses.has(ChatStatus.OnHold)) {
                selectionState.hold(...ids);
            }
        }

        setDragGroup(undefined);
        setDragParentColumn(undefined);
        if (newSortBy !== sortBy) {
            updateColumns({
                sortBy: newSortBy,
            });
        }
    };

    const dragStart = (draggableId: string) => {
        if (draggableId.startsWith("urn:fq:ticket-state")) {
            return;
        }
        const chat = selectionState.getChat(draggableId);

        const state = selectionState.getChatState(draggableId);
        if (!chat || !state) {
            return;
        }
        const selected = selectionState.getSelection();
        if (!selected.some(row => row.id === draggableId)) {
            selectionState.clearSelection();
        }

        const groupBy = statesData?.dashboard?.settings?.groupBy ?? DashboardGroupBy.User;
        const chatGroup = createGroup(groupBy, chat, state);

        const unselect = selected.filter(item => selectionState.getGroup(item.id)?.fullId !== chatGroup?.fullId);

        if (unselect.isNotEmpty) {
            selectionState.unselectAll(...unselect.map(item => item.id));
        }

        selectionState.ensureSelected(draggableId);
        if (chat.status !== ChatStatus.Finished && chat.status !== ChatStatus.Ignored) {
            setShowActionStates(true);
        }
        const column = selectionState.getColumn(chat.id);
        if (column) {
            setDragParentColumn(column?.id);
        }

        if (chatGroup) {
            setDragGroup(chatGroup.id);
        }
    };

    const onColumnChangeName = (id: string, name: string) => {
        const column = columns.find(col => col.id === id);
        if (column) {
            column.text = name;
        }

        updateColumns();
    };

    const isWaiting = selectionState.selected?.status === ChatStatus.ChatRequested
                    || selectionState.selected?.status === ChatStatus.Connected;

    return (

        <div className="h-100 d-flex flex-column">
            <TopOptions
                refreshColumns={refreshColumns}
                updateColumns={updateColumns}
                settings={statesData.dashboard?.settings as DashboardSettings}
                search={setSearch}
                onChangeSearch={setSearchFilters}
            />

            <Chat />

            <DragDropContext
                nonce={window.NONCE}
                onDragEnd={onDragEnd}
                onBeforeCapture={drag => dragStart(drag.draggableId)}

            >
                {showActionStates && (
                    <div
                        title="Actions"
                        className={`p-0 m-0  ${Styles["states-container"]} border-0 `}
                    >
                        <div className="d-flex align-items-stretch w-100 h-100 justify-content-between">
                            <DropAction
                                dragGroup={dragGroup}
                                action={isWaiting ? Action.ASSIGN : Action.TRANSFER}
                                text={isWaiting ? "Assign" : "Transfer"}
                                variant="primary"
                            />
                            <DropAction
                                dragGroup={dragGroup}
                                action={isWaiting ? Action.IGNORE : Action.END_CHAT}
                                text={isWaiting ? "Ignore" : "End"}
                                variant="danger"

                            />
                        </div>

                    </div>
                )}

                <Droppable
                    key="drop-columns"
                    droppableId="drop-columns"
                    mode="standard"
                    direction={view === DashboardView.Compact ? "vertical" : "horizontal"}
                    type="column-drag"
                >
                    {dropStoppable => {
                        const classNames = [];
                        if (view === DashboardView.Wall) {
                            classNames.push(ColumnStyles["display-wall"]);
                        } else if (view === DashboardView.Kanban || view === DashboardView.KanbanWrapped) {
                            classNames.push(`d-flex flex-column flex-lg-row overflow-auto align-items-start display-kanban`);
                        } else if (view === DashboardView.Compact) {
                            classNames.push(ColumnStyles["display-compact"]);
                        }
                        if (view === DashboardView.KanbanWrapped || dragParentStateId) {
                            classNames.push("flex-wrap");
                        }

                        // if (dragGroup) {
                        //     classNames.push(Styles["fixed-columns"]);
                        // }
                        return (
                            <div
                                className={`${classNames.join(" ")} flex-grow-1`}
                                ref={dropStoppable.innerRef}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...dropStoppable.droppableProps}
                            >

                                {columns.map((column, index) => (
                                    <Column
                                        hasGroups={statesData.dashboard?.settings?.groupBy !== DashboardGroupBy.NoGroup}
                                        canDrag={view !== DashboardView.Wall}
                                        key={column.id}
                                        column={column}
                                        index={index}
                                        dragGroup={dragGroup}
                                        onColumnChangeName={onColumnChangeName}
                                        selectionState={selectionState}
                                        view={view ?? DashboardView.Kanban}
                                        groupBy={statesData.dashboard?.settings?.groupBy ?? DashboardGroupBy.User}
                                        searchValue={search}
                                        ticketView={statesData.dashboard?.settings?.ticketView ?? TicketView.MyTickets}
                                        hasSlas={hasSlas ?? false}
                                        dragParentStateId={dragParentStateId}
                                    />
                                ))}

                                {!isSearching && view === DashboardView.Compact && columns.sum(row => row.items.length) === 0 && (
                                    <div className="text-center mt-4 text-light fs-5">
                                        Looks like your queue is empty 🎉.
                                    </div>
                                )}

                                <SearchResults
                                    groupBy={statesData.dashboard?.settings?.groupBy ?? DashboardGroupBy.NoGroup}
                                    searchValue={search}
                                    dashboardId={dashboardId}
                                    displayedItems={isSearching ? columns.flatMap(col => col.items.toIdList("id")) : []}
                                    ticketView={statesData.dashboard?.settings?.ticketView ?? TicketView.MyTickets}
                                    searchFilters={searchFilters}
                                />

                            </div>

                        );
                    }}
                </Droppable>

            </DragDropContext>
            <Snooze />
            <Hold />
            <Ignore />
            <EndChat />
            <Transfer />
            <EasterEgg />
            <ParticipantsAdd />
            <ParticipantsLeave />
            <MarkRead />
            <MarkUnread />
            <SetTicketState />
            <StartConversation />
            <Confirm onHide={() => setErrorMessage(undefined)} shouldShow={errorMessage != null} closeText="Cancel">
                <div className="alert alert-danger">
                    {errorMessage}
                </div>
            </Confirm>
            {statesData.dashboard?.settings?.lastConversationCount !== 0 && (
                <LatestConversations displayCount={statesData.dashboard?.settings?.lastConversationCount ?? 5} />
            )}
        </div>

    );
}
